/**
 * **Die Sync-Buchführung einer Zeile** — `synced`, `updatedAt`, `lastModified`
 * — und der Schalter, der sie stillstellt.
 *
 * Eigenes Modul, weil zwei Seiten ihn brauchen, die einander nicht importieren
 * dürfen: `indexeddb.ts` (wo die Dexie-Hooks hängen, die stempeln) und
 * `schema.ts` (wo die Migrationen laufen, die nicht stempeln sollen).
 * `indexeddb.ts` importiert `schema.ts` — der Zähler hier gehört keinem von
 * beiden und schließt den Kreis nicht.
 */

/**
 * Zähler statt Bool. `withSyncWrite`/`withoutSyncBookkeeping`-Aufrufe können
 * sich zeitlich überlappen — IndexedDB ist async (jedes `.update()` braucht
 * mindestens ein `get` und ein `put`, mit Microtask-Lücken dazwischen), und
 * zwei ganz unabhängige Aufrufer (z. B. ein Pull-Zyklus UND eine
 * Notiz-Messung) können dazwischenfunken. Mit einem reinen Bool setzte der
 * zuerst FERTIGE Aufruf das Flag zurück, während der andere Aufruf seinen
 * `updating`-Hook noch vor sich hat — der liefe dann UNGESCHÜTZT durch und
 * stempelte doch `synced`/Zeitstempel. Der Zähler bleibt > 0, solange
 * mindestens ein Aufruf noch läuft, unabhängig davon, in welcher Reihenfolge
 * sie sich beenden.
 */
let suppressDepth = 0

export function isSyncBookkeepingSuppressed(): boolean {
  return suppressDepth > 0
}

async function suppressSyncBookkeeping<T>(fn: () => Promise<T>): Promise<T> {
  suppressDepth++
  try {
    return await fn()
  } finally {
    suppressDepth--
  }
}

/**
 * Execute a DB write inside a sync-write context.
 * The Dexie updating hooks will be skipped so the sync engine
 * can set `synced`, `updatedAt`, etc. without interference.
 */
export function withSyncWrite<T>(fn: () => Promise<T>): Promise<T> {
  return suppressSyncBookkeeping(fn)
}

/**
 * Führt einen DB-Schreibvorgang aus, der die Sync-Buchführung der Zeile GAR
 * NICHT anfassen soll — weder `synced` noch `updatedAt`/`lastModified`.
 *
 * Anders als `withSyncWrite`: Dort SETZT der Aufrufer diese Felder selbst
 * (der Sync-Engine übernimmt die Buchführung explizit, z. B. `synced: true`
 * nach einem erfolgreichen Pull). Hier setzt niemand etwas — der Aufrufer
 * schreibt ein Feld, das keine Entscheidung des Nutzers ist (z. B. die
 * gemessene Höhe einer **Notiz**, siehe `setCanvasNoteMeasuredHeight`), und
 * die Zeile soll danach dastehen, als wäre nichts geschrieben worden: nicht
 * als „geändert seit dem letzten Sync" markiert, ohne neuen Zeitstempel.
 *
 * Technisch dasselbe wie `withSyncWrite` (derselbe Zähler, siehe oben) — der
 * eigene Name sagt am Aufrufort, WARUM unterdrückt wird, nicht bloß DASS.
 */
export function withoutSyncBookkeeping<T>(fn: () => Promise<T>): Promise<T> {
  return suppressSyncBookkeeping(fn)
}

/**
 * **Eine Migration schreibt keine Nutzeränderung.**
 *
 * Dexies `modify` schreibt JEDE Zeile der Tabelle zurück, auch wenn der
 * Rückruf nichts ändert — ein `if (!('tape' in note))` spart den Feldwert,
 * nicht den Schreibvorgang. Ohne diese Klammer liefe der `updating`-Hook über
 * die ganze Tabelle und setzte überall `synced = false` samt neuem
 * `updatedAt`.
 *
 * Der Schaden daraus ist nicht kosmetisch (beobachtet am 07.09.2026, nach den
 * Migrationen v28–v31 über 845 canvasNote-Zeilen):
 *
 * 1. **Nichts geht mehr hoch.** Der Push liest die `syncQueue`; ein Bulk-Write
 *    legt dort keinen Eintrag an. Die Zeilen stehen als „geändert" da, und
 *    niemand trägt sie nach.
 * 2. **Ältere Fernänderungen verlieren dauerhaft.** Jede Cloud-Änderung mit
 *    einem Stempel vor der Migration verliert den LWW-Vergleich
 *    `remoteUpdated > updatedAt` (`sync-pull.ts`) gegen die künstlich
 *    vorgerückte Zeit — nicht nur einmal, sondern für immer.
 *
 * **Jede** `upgrade`-Migration, die Zeilen anfasst, gehört hier hinein.
 */
export function migrationWrite<T>(fn: () => Promise<T>): Promise<T> {
  return suppressSyncBookkeeping(fn)
}
